import type { AuthState } from '@/features/auth/authSlice';

/**
 * servicePointUtils.ts
 *
 * Utilities for interpreting UMEX user role/limitation data and persisting the
 * selected service point in localStorage.
 *
 * Current responsibilities:
 * - identify whether the current login role should behave as admin or non-admin,
 * - extract direct limitation values already present in the UMEX payload,
 * - expose country/service-point metadata used by the login and dashboard flows,
 * - persist the selected service point id/profile across refreshes.
 *
 * Important:
 * - This file does NOT build the final scoped `_search` request anymore.
 * - Hierarchy-based scope resolution (`service point -> cluster -> zone -> country`)
 *   is handled by `buildServicePointSearchScope()` in `src/utils/userScope.ts`
 *   and consumed by `loadScopedServicePoints()`.
 * - This file is now focused on raw UMEX extraction helpers + local persistence.
 */

/**
 * Roles treated as admin during login.
 *
 * Admin users do not need a default service point selected during login and can
 * proceed directly to the main experience.
 */
export const ADMIN_ROLE_NAMES: ReadonlyArray<string> = [
    'Regional Retail Service Owner',
    'Country Retail Manager',
    'Super Admin',
];

/** Raw limitation item returned by UMEX for a role. */
interface UmexDataLimitation {
    id: number;
    type: string;
    objectId: string;
    code: string;
    name: string;
}

/** Minimal UMEX role shape consumed by the login/service-point flow. */
export interface UmexRole {
    id: number;
    name: string;
    label?: string;
    userDataLimitations?: {
        userDataLimitations: UmexDataLimitation[];
    };
}

/** Reuse the typed auth user shape from Redux store instead of `Record<string, any>`. */
type UmexUser = NonNullable<AuthState['user']>;

/** Returns the first role object from the user's roles array. */
function getPrimaryRole(user: UmexUser | null | undefined): UmexRole | null {
    return user?.roles?.roles?.[0] ?? null;
}

/**
 * Returns true when the primary role is NOT in the admin role allowlist.
 *
 * In the current login flow this means the user is treated as a non-admin user
 * and may need service-point resolution/selection before entering the app.
 */
export function isAgentRole(user: UmexUser | null | undefined): boolean {
    const role = getPrimaryRole(user);
    if (!role) return false;
    const roleName: string = role.name ?? role.label ?? '';
    return !ADMIN_ROLE_NAMES.includes(roleName);
}

/**
 * Extracts direct `Service Point` limitations from the UMEX payload.
 *
 * Returns a deduplicated list of direct service-point assignments already
 * present on the user object. This is useful for role/selection metadata, but
 * it is not the full source of truth for login-time scope resolution anymore.
 * Users may now receive access through higher-level limitations such as:
 * - Region
 * - Country
 * - Zone
 * - Cluster
 * - Service Point
 *
 * The final service-point list shown to the user is resolved later through the
 * scoped `_search` flow when direct service-point limitations are not present.
 */
export function extractServicePoints(
    user: UmexUser | null | undefined,
): Array<{ id: string; code: string; name: string }> {
    const roles: UmexRole[] = user?.roles?.roles ?? [];
    const seen = new Set<string>();
    const result: Array<{ id: string; code: string; name: string }> = [];

    for (const role of roles) {
        const limitations: UmexDataLimitation[] =
            role.userDataLimitations?.userDataLimitations ?? [];
        for (const lim of limitations) {
            if ((lim.type ?? '').toLowerCase() === 'service point' && !seen.has(lim.code)) {
                seen.add(lim.code);
                result.push({
                    id: lim.objectId,
                    code: lim.code,
                    name: lim.name,
                });
            }
        }
    }

    return result;
}

/**
 * Extracts the first assigned country code from UMEX limitations.
 *
 * Returns the first match (e.g. `MY`) or an empty string when the user has no
 * country limitation in the raw payload.
 */
export function extractCountryCode(user: UmexUser | null | undefined): string {
    const roles: UmexRole[] = user?.roles?.roles ?? [];
    for (const role of roles) {
        const limitations: UmexDataLimitation[] =
            role.userDataLimitations?.userDataLimitations ?? [];
        const country = limitations.find((lim) => (lim.type ?? '').toLowerCase() === 'country');
        if (country) return country.code;
    }
    return '';
}

/** Returns true when the user's assigned country is not enabled for NOVA. */
export function isAssignedCountryNonNova(
    user: UmexUser | null | undefined,
    countries: Array<{ isoCtryCode?: string; cd?: string; onboarded?: string }>,
): boolean {
    const countryCode = extractCountryCode(user).toUpperCase();
    if (!countryCode || countries.length === 0) return false;

    const country = countries.find(
        (item) => (item.isoCtryCode ?? item.cd ?? '').toUpperCase() === countryCode,
    );
    return !country || (country.onboarded ?? 'N').toUpperCase() !== 'Y';
}

/**
 * Extracts all assigned countries from UMEX limitations.
 *
 * Returns a deduplicated list of `{ code, name }` objects and is primarily used
 * by service-point and country-selection UI to constrain visible countries.
 */
export function extractCountries(
    user: UmexUser | null | undefined,
): Array<{ code: string; name: string }> {
    const roles: UmexRole[] = user?.roles?.roles ?? [];
    const seen = new Set<string>();
    const result: Array<{ code: string; name: string }> = [];
    for (const role of roles) {
        const limitations: UmexDataLimitation[] =
            role.userDataLimitations?.userDataLimitations ?? [];
        for (const lim of limitations) {
            if ((lim.type ?? '').toLowerCase() === 'country' && !seen.has(lim.code)) {
                seen.add(lim.code);
                result.push({ code: lim.code, name: lim.name });
            }
        }
    }
    return result;
}

// ---------------------------------------------------------------------------
// localStorage persistence for the selected service-point id.
// Written during login and dashboard service-point changes so the user's
// selected service point survives refreshes and can seed Redux on reload.
// ---------------------------------------------------------------------------

/** localStorage key for the selected service-point id. */
const SP_CACHE_KEY = 'rwf_selected_sp';

/** Read the cached selected service-point id from localStorage. */
export function getCachedServicePointId(): string | null {
    try {
        return localStorage.getItem(SP_CACHE_KEY);
    } catch {
        return null;
    }
}

/** Persist the selected service-point id to localStorage. */
export function setCachedServicePointId(id: string): void {
    try {
        localStorage.setItem(SP_CACHE_KEY, id);
    } catch (err) {
        console.warn('[servicePointUtils] localStorage unavailable:', err);
    }
}

/** Clear the cached service-point id when the user explicitly clears selection. */
export function clearCachedServicePointId(): void {
    try {
        localStorage.removeItem(SP_CACHE_KEY);
    } catch (err) {
        console.warn('[servicePointUtils] localStorage unavailable:', err);
    }
}
